<template>
  <View direction="column" gap="lg">
    <View direction="column" gap="xs">
      <Text variant="h2">Company profile</Text>
      <Text variant="body-large" color="secondary">What renters see on your listings and showing pages</Text>
    </View>

    <View v-if="isLoading" justify="center" padding="lg">
      <LoadingIndicator />
    </View>

    <template v-else>
      <ImageUploadField
        :image-url="company?.logo"
        :alt="company?.name || 'Company logo'"
        upload-label="Upload logo"
        hint="Shown on renter-facing pages"
        :is-uploading="isUploadingLogo"
        :is-removing="isRemovingLogo"
        @pick="uploadLogo"
        @remove="removeLogo"
      >
        <template #placeholder>{{ companyInitial }}</template>
      </ImageUploadField>

      <Divider />

      <View direction="column" gap="md">
        <FormField label="Company name" :error="form.fieldErrors('name')">
          <Input v-model="form.fields.name" placeholder="Your company name" />
        </FormField>

        <div class="contact-grid">
          <FormField label="Public contact email" :error="form.fieldErrors('contactEmail')">
            <Input v-model="form.fields.contactEmail" type="email" placeholder="hello@yourcompany.com" />
          </FormField>
          <FormField label="Public phone" :error="form.fieldErrors('contactPhone')">
            <InputPhone v-model="form.fields.contactPhone" />
          </FormField>
        </div>

        <Text variant="body-small" color="tertiary">
          Renters contact these instead of your personal email and phone.
        </Text>
      </View>

      <Divider />

      <View justify="end">
        <Button :loading="form.submitting" @click="saveChanges">Save changes</Button>
      </View>
    </template>
  </View>
</template>

<script setup lang="ts">
import { email as emailValidator, helpers, required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

import {
  Button,
  Divider,
  extractErrorMessage,
  FormField,
  Input,
  InputPhone,
  LoadingIndicator,
  phoneNumberValidator,
  Text,
  useAlertsStore,
  useForm,
  View,
} from '@tallo/design-system';
import { ImageUploadField } from '@tallo/file-manager';
import { companyService, useCompanyStore } from '@tallo/investor/company';
import { normalizePhoneNumber } from '@tallo/utility';

import { useSeededForm } from '../composables/use-seeded-form';

const companyStore = useCompanyStore();
const alertsStore = useAlertsStore();
const { company, isLoading } = storeToRefs(companyStore);

const isUploadingLogo = ref(false);
const isRemovingLogo = ref(false);

const companyInitial = computed(() => (company.value?.name || 'C').charAt(0).toUpperCase());

const form = useForm(
  {
    name: company.value?.name || '',
    contactEmail: company.value?.contactEmail || '',
    contactPhone: company.value?.contactPhone || '',
  },
  {
    name: {
      required: helpers.withMessage('Company name is required', required),
    },
    contactEmail: {
      email: helpers.withMessage('Please enter a valid email address', emailValidator),
    },
    contactPhone: {
      phoneNumberValidator,
    },
  },
);

const isDirty = computed(
  () =>
    form.fields.name !== (company.value?.name || '') ||
    form.fields.contactEmail.trim() !== (company.value?.contactEmail || '') ||
    normalizePhoneNumber(form.fields.contactPhone) !== normalizePhoneNumber(company.value?.contactPhone),
);

useSeededForm(company, reset, isDirty);

function reset() {
  form.fields.name = company.value?.name || '';
  form.fields.contactEmail = company.value?.contactEmail || '';
  form.fields.contactPhone = company.value?.contactPhone || '';
  form.resetValidation();
}

function saveChanges() {
  form.submit(async () => {
    try {
      const updated = await companyService.updateInfo({
        name: form.fields.name,
        contactEmail: form.fields.contactEmail.trim() || null,
        contactPhone: form.fields.contactPhone || null,
      });
      companyStore.setCompany(updated);
      reset();
      alertsStore.success('Changes saved');
    } catch (error) {
      alertsStore.error(extractErrorMessage(error, 'Failed to save changes'));
    }
  });
}

async function uploadLogo(file: File) {
  isUploadingLogo.value = true;

  try {
    companyStore.setCompany(await companyService.uploadLogo(file));
    alertsStore.success('Logo updated');
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not upload the logo'));
  } finally {
    isUploadingLogo.value = false;
  }
}

async function removeLogo() {
  isRemovingLogo.value = true;

  try {
    companyStore.setCompany(await companyService.removeLogo());
    alertsStore.success('Logo removed');
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not remove the logo'));
  } finally {
    isRemovingLogo.value = false;
  }
}

onMounted(() => {
  companyStore.load();
});

defineExpose({ isDirty, reset });
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.contact-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--t-spacing-md);

  // Matches the field grids: 2-col only in the roomy sidebar layout, 1-col whenever narrow/collapsed
  @include screen-min-lg {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
